import { Alert, Button, Stack } from '@mui/material'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import { tokens } from '@/theme'

const LABELS: Record<string, string> = {
  google: 'Continue with Google',
}

/**
 * The sign-in buttons, wherever somebody needs to sign in.
 *
 * Extracted because /signin is nothing but these buttons, so sending somebody
 * there from the join or pay page spent a whole navigation to show them the
 * same two controls. They belong on the page that needs them.
 *
 * The provider list comes from the server, never a constant here: a provider
 * appears only once BOTH its credentials are configured, so a half-configured
 * deploy shows nothing rather than a button that dead-ends.
 */
export function ProviderButtons({ next }: { next: string }) {
  const providers = useQuery({
    queryKey: ['providers'],
    queryFn: () => api.get<{ providers: string[] }>('/auth/providers'),
  })

  // A failed request used to render NOTHING. `data` is undefined, so the map
  // never runs and `length === 0` is false against undefined — both branches
  // silently declined, leaving a gap above the divider where the buttons go.
  // From the outside that is indistinguishable from a server with no provider
  // configured, and it is the likelier of the two: the endpoint is one request
  // on the one page somebody is trying to get past.
  if (providers.isError) {
    return (
      <Alert
        severity="warning"
        sx={{ fontSize: '0.8125rem' }}
        action={
          <Button size="small" onClick={() => providers.refetch()}>
            Retry
          </Button>
        }
      >
        Could not load the sign-in buttons. Use a code below, or try again.
      </Alert>
    )
  }

  // Nothing while it is in flight. Buttons that appear a moment late are
  // ordinary; a spinner where a button is about to be is a flicker, and this
  // request is one small GET against the same origin.
  if (!providers.data) return null

  const names = providers.data.providers

  return (
    <Stack spacing={1}>
      {names.map((name) => (
        <Button
          key={name}
          variant="outlined"
          size="large"
          href={`/auth/${name}/login?next=${encodeURIComponent(next)}`}
          sx={{
            justifyContent: 'center',
            minHeight: 42,
            borderColor: tokens.line,
            color: 'text.primary',
          }}
        >
          {LABELS[name] ?? name}
        </Button>
      ))}

      {names.length === 0 && (
        <Alert severity="warning" sx={{ fontSize: '0.8125rem' }}>
          No sign-in provider is configured on this server.
        </Alert>
      )}
    </Stack>
  )
}
